/**
 * The ground layer — the shore, and the snow lying on the tops.
 *
 * ## Why a fourth raster and not more work in the three there are
 *
 * The chart already bakes three: the **relief** says which way a slope faces
 * and may only lighten and darken; the **fabric** says what grows on the ground
 * and may only darken; the **shelf** is about the water. None of them can say
 * *this ground is a different colour from the ground beside it*, and that is the
 * whole of what a beach and a snowfield are. A beach drawn as a dark speckle is
 * not a beach, and snow drawn as a white highlight is a bright slope.
 *
 * So this one composites normally over the province fills, and is laid **under**
 * the relief and the fabric rather than over them. That order is the point: a
 * strand still takes the light from the north-west, a snowfield still has the
 * crags in it, a cliff top still shades. A pale layer over the top of the
 * hillshade would have flattened all three.
 *
 * ## What it is derived from
 *
 * Nothing new, which is the standing rule on this chart. `RASTER.owner` gives
 * the land mask, `RASTER.offshore` gives the distance out to sea, a chamfer of
 * the same mask gives the matching distance inland, `RASTER.height`'s gradient
 * at the water's edge decides whether a shore is a beach or a cliff, and
 * `wasteness` — the generator's own latitude term — decides where the snowline
 * runs. Nothing here invents a field and nothing here is hand-placed.
 *
 * **Measured, before any of it was drawn.** At the 4,762 land cells within a
 * cell and a half of water, |∇h| runs p25 0.0036, median 0.0063, p75 0.0144,
 * p90 0.0238, p99 0.0400 per map unit. `CLIFF_LO` and `CLIFF_HI` are set
 * against those percentiles, so about a quarter of the world's coast comes out
 * cliff and the rest sand — and `npm run coastline` prints the split rather
 * than leaving it to be believed.
 *
 * **What invalidates the bake: nothing at runtime.** It is a function of the
 * raster and the province table, both of which are content, so it is computed
 * once on first ask and kept for the session exactly as the other three are.
 */

import { RASTER, WORLD } from '../data/realm.ts';
import { provinceDef } from '../data/provinces.ts';
import type { ProvinceTerrain } from '../data/realm.ts';
import { geometry } from '../data/cartography.ts';
import type { Pt } from '../data/cartography.ts';
import { LIGHT_X, LIGHT_Y, fbm, lakeDepth, planeSource, snowAt, vnoise2 } from './relief.ts';

/** Pixels to a raster cell. Two, for the same reason the fabric is two. */
const SHORE_SS = 2;

/**
 * How far inland a strand may reach, in map units — and where it starts.
 *
 * `STRAND_EDGE` is the correction the first cut needed. The chamfer measures to
 * the nearest *water cell centre*, so the first land cell is already a whole
 * cell — two map units — from the water, and a ramp written as `1 - s/STRAND`
 * therefore peaked at 0.6 rather than 1 and put its strongest sand two cells
 * inland. Photographed at the flattest shore on the board, the beach was
 * invisible. The ramp starts at the water's edge now.
 */
const STRAND = 2.8;
const STRAND_EDGE = 1.6;

/**
 * Where wet ground starts and where it becomes water, in the height field's own
 * units — the units `lakeDepth` is measured in.
 *
 * Set against the measured distribution rather than by feel, which
 * `npm run coastline` prints: of the land, **11.6% holds more than 0.002,
 * 7.9% more than 0.02, 4.7% more than 0.05.** So a fen that begins at 0.006
 * covers about a tenth of the board — which is what a world with this much
 * lowland and this much rain should have — and open water that only arrives at
 * 0.045 and is not full until 0.09 keeps the meres to the few dozen basins deep
 * enough to be lakes rather than to every hollow that holds a puddle.
 */
const FEN_FROM = 0.006;
const FEN_FULL = 0.04;
const MERE_FROM = 0.045;
const MERE_FULL = 0.09;
/** …and how far out the surf runs. */
const SURF = 1.35;

/**
 * Where a shore stops being a beach and starts being a cliff, in |∇h| per map
 * unit. `CLIFF_LO` sits a little over the median of the measured shore and
 * `CLIFF_HI` between its p90 and its p99 — see the header.
 */
export const CLIFF_LO = 0.009;
export const CLIFF_HI = 0.03;

let signedCache: Float32Array | null = null;

/**
 * Distance to the shore over the whole raster: positive inland, negative at sea.
 *
 * The seaward half is `RASTER.offshore`, which has existed since the raster did
 * and which the shelf and the sea lanes already read. The landward half is a
 * two-pass chamfer of the same mask — the standard 3-4 approximation to
 * Euclidean distance, one sweep down-right and one up-left — which is exact
 * enough at a pitch of two map units and costs two passes over 166,000 cells
 * instead of a flood per cell.
 */
export function shoreDistance(): Float32Array {
  if (signedCache) return signedCache;
  const { cols, rows, cell, owner, offshore } = RASTER;
  const n = cols * rows;
  const d = new Float32Array(n);
  const INF = 1e9;
  for (let k = 0; k < n; k++) d[k] = owner[k] < 0 ? 0 : INF;
  const a = cell;
  const b = cell * Math.SQRT2;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const k = r * cols + c;
      let v = d[k];
      if (v === 0) continue;
      if (c > 0) v = Math.min(v, d[k - 1] + a);
      if (r > 0) v = Math.min(v, d[k - cols] + a);
      if (r > 0 && c > 0) v = Math.min(v, d[k - cols - 1] + b);
      if (r > 0 && c < cols - 1) v = Math.min(v, d[k - cols + 1] + b);
      d[k] = v;
    }
  }
  for (let r = rows - 1; r >= 0; r--) {
    for (let c = cols - 1; c >= 0; c--) {
      const k = r * cols + c;
      let v = d[k];
      if (v === 0) continue;
      if (c < cols - 1) v = Math.min(v, d[k + 1] + a);
      if (r < rows - 1) v = Math.min(v, d[k + cols] + a);
      if (r < rows - 1 && c < cols - 1) v = Math.min(v, d[k + cols + 1] + b);
      if (r < rows - 1 && c > 0) v = Math.min(v, d[k + cols - 1] + b);
      d[k] = v;
    }
  }
  for (let k = 0; k < n; k++) if (owner[k] < 0) d[k] = -offshore[k];
  signedCache = d;
  return d;
}

let cliffCache: Float32Array | null = null;

/**
 * How much of a cliff each cell's nearest shore is, 0..1.
 *
 * Measured only on the land cells that actually touch the water — the gradient
 * three cells inland is a statement about a hillside, not about a coast — and
 * then spread outward, so that the water at a cliff's foot and the ground on
 * its top both know what kind of shore they belong to. Spread by a max filter
 * rather than by a nearest-shore index, because the question being asked is "is
 * there a cliff within reach of me", and then blurred twice so that a headland
 * does not arrive in square steps.
 */
export function shoreCliff(): Float32Array {
  if (cliffCache) return cliffCache;
  const { cols, rows, cell, owner, height } = RASTER;
  const n = cols * rows;
  const sd = shoreDistance();
  let f = new Float32Array(n);
  for (let r = 1; r < rows - 1; r++) {
    for (let c = 1; c < cols - 1; c++) {
      const k = r * cols + c;
      if (owner[k] < 0 || sd[k] > cell * 1.5) continue;
      const gx = (height[k + 1] - height[k - 1]) / (2 * cell);
      const gy = (height[k + cols] - height[k - cols]) / (2 * cell);
      const g = Math.hypot(gx, gy);
      f[k] = Math.max(0, Math.min(1, (g - CLIFF_LO) / (CLIFF_HI - CLIFF_LO)));
    }
  }
  // Four dilations at two map units apiece reach eight units each way, which
  // covers the strand inland and the surf out to sea with a little to spare.
  for (let pass = 0; pass < 4; pass++) {
    const g = new Float32Array(n);
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        let m = f[r * cols + c];
        for (let dy = -1; dy <= 1; dy++) {
          const ry = r + dy;
          if (ry < 0 || ry >= rows) continue;
          for (let dx = -1; dx <= 1; dx++) {
            const rx = c + dx;
            if (rx < 0 || rx >= cols) continue;
            const v = f[ry * cols + rx];
            if (v > m) m = v;
          }
        }
        g[r * cols + c] = m;
      }
    }
    f = g;
  }
  for (let pass = 0; pass < 2; pass++) {
    const g = new Float32Array(n);
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        let s = 0;
        let w = 0;
        for (let dy = -1; dy <= 1; dy++) {
          const ry = r + dy;
          if (ry < 0 || ry >= rows) continue;
          for (let dx = -1; dx <= 1; dx++) {
            const rx = c + dx;
            if (rx < 0 || rx >= cols) continue;
            s += f[ry * cols + rx];
            w++;
          }
        }
        g[r * cols + c] = s / w;
      }
    }
    f = g;
  }
  cliffCache = f;
  return f;
}

/** Bilinear sample of a per-cell field, in raster-cell coordinates. */
function sampleField(field: Float32Array, x: number, y: number): number {
  const { cols, rows } = RASTER;
  const cx = Math.max(0, Math.min(cols - 1.001, x));
  const cy = Math.max(0, Math.min(rows - 1.001, y));
  const x0 = cx | 0;
  const y0 = cy | 0;
  const tx = cx - x0;
  const ty = cy - y0;
  const k = y0 * cols + x0;
  const a = field[k];
  const b = field[k + 1];
  const c = field[k + cols];
  const e = field[k + cols + 1];
  return (a + (b - a) * tx) * (1 - ty) + (c + (e - c) * tx) * ty;
}

let groundCached = '';

/** The strand, the cliffs, the surf and the snow, as one data URL. */
export function groundImage(): string {
  if (groundCached !== '') return groundCached;
  const { cols, rows, cell, originY, owner, height } = RASTER;
  const w = cols * SHORE_SS;
  const h = rows * SHORE_SS;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  const img = ctx.createImageData(w, h);
  const d = img.data;

  const sd = shoreDistance();
  const cliffField = shoreCliff();
  const wetField = lakeDepth();
  const kindOf = new Map<number, ProvinceTerrain>();

  for (let py = 0; py < h; py++) {
    for (let px = 0; px < w; px++) {
      /**
       * Through the warp backwards, and this is the one bake where it changes
       * what the picture *says* rather than only where it says it.
       *
       * The strand is a hard change of colour at the water's edge and the
       * coastline is a pen drawn over it. Measured before this pass, the two
       * were a median of two map units apart and as much as six — so the beach
       * lay in the sea along one stretch of shore and a pen's width inland
       * along the next, and no amount of tuning `STRAND` could have found it.
       * `planeSource` puts them in the same coordinate system. See the note at
       * the head of `relief.ts`.
       */
      const src = planeSource(px / SHORE_SS, py / SHORE_SS);
      const fx = src.x;
      const fy = src.y;
      const k =
        Math.max(0, Math.min(rows - 1, fy | 0)) * cols + Math.max(0, Math.min(cols - 1, fx | 0));
      const o = (py * w + px) * 4;
      const land = owner[k] >= 0;

      const s = sampleField(sd, fx, fy);
      if (!land && s < -SURF) {
        d[o + 3] = 0;
        continue;
      }

      // Accumulated in float and composited in order, because one pixel can be
      // a beach and under snow and in a cliff's shadow at once, and picking the
      // loudest of the three is how a layer grows an edge nobody drew.
      let rr = 0;
      let gg = 0;
      let bb = 0;
      let aa = 0;
      /**
       * Source-over in **straight** alpha, and the distinction is not academic.
       *
       * The first cut of this wrote `rr = r * al + rr * (1 - al)`, which is the
       * *premultiplied* form — and `putImageData` wants straight RGBA. So every
       * colour arrived multiplied by its own alpha: surf at alpha 0.2 was
       * written as (43,46,47) instead of (216,232,236), sand at 0.5 as a muddy
       * brown, snow as a mid grey. Counted off the baked PNG, the layer was
       * painting **38,831 near-black pixels against 4,638 sandy ones**, and the
       * beach the whole pass exists for was arriving as a smear of soot.
       *
       * Dividing the accumulated colour back out by the accumulated alpha is
       * the whole fix, and it is the sort of fault that is invisible in the code
       * and obvious the moment somebody counts the pixels.
       */
      const over = (r: number, g: number, b: number, a: number): void => {
        const al = Math.min(1, a);
        if (al <= 0) return;
        const na = al + aa * (1 - al);
        if (na <= 0) return;
        rr = (r * al + rr * aa * (1 - al)) / na;
        gg = (g * al + gg * aa * (1 - al)) / na;
        bb = (b * al + bb * aa * (1 - al)) / na;
        aa = na;
      };
      const put = (): void => {
        d[o] = rr | 0;
        d[o + 1] = gg | 0;
        d[o + 2] = bb | 0;
        d[o + 3] = Math.min(255, aa * 255) | 0;
      };

      const cliff = sampleField(cliffField, fx, fy);

      /**
       * Which way the shore faces, and therefore whether the light finds it.
       *
       * The gradient of the distance field points inland, so its negative is
       * the shore's outward normal in plan. Dotted against the same north-west
       * light everything else in this game is lit by, that one number decides
       * whether a headland is a bright wall or a dark one. It is the coastal
       * case of exactly the rule the hillshade applies to a hillside, and it has
       * to agree with it — `drawBuilding` lights its left face and so does this.
       */
      /**
       * Sampled **three cells apart, not one**, and the width is the fix.
       *
       * The coastline this raster is traced from runs in straight lattice
       * stretches with sharp corners between them, so a gradient measured over
       * one cell flips through ninety degrees at every corner — and the cliff
       * shading, which is a function of that gradient, came out as a row of
       * alternating dark and bright beads along the shore. Photographed at two
       * notches in it looked like a string of pearls. Six map units is wide
       * enough to average a corner out and still narrow enough to tell one
       * headland from the bay beside it.
       */
      const gx = sampleField(sd, fx + 1.5, fy) - sampleField(sd, fx - 1.5, fy);
      const gy = sampleField(sd, fx, fy + 1.5) - sampleField(sd, fx, fy - 1.5);
      const gl = Math.hypot(gx, gy) || 1;
      const facing = (gx / gl) * LIGHT_X + (gy / gl) * LIGHT_Y;

      if (!land) {
        /**
         * Surf on a beach, shadow under a cliff.
         *
         * A gentle shore breaks white a long way out and a cliff does not break
         * at all — it throws the water at its foot into shade instead, on
         * whichever side the light is not. Both are the same band of water
         * saying which kind of coast it belongs to, which is the thing a
         * shoreline has to say before anything else on it registers.
         */
        const t = 1 + s / SURF;
        over(187, 199, 183, t * t * (1 - cliff) * 0.28);
        if (facing < 0) over(6, 10, 14, t * t * cliff * -facing * 0.4);
        put();
        continue;
      }

      let kind = kindOf.get(owner[k]);
      if (kind === undefined) {
        kind = provinceDef(owner[k]).terrain;
        kindOf.set(owner[k], kind);
      }

      if (s < STRAND) {
        const t = Math.max(0, Math.min(1, 1 - (s - STRAND_EDGE) / STRAND));
        /**
         * Sand, and only where the ground lets it lie.
         *
         * Broken along its length by a coarse noise rather than laid on evenly:
         * a real strand is bays of sand between rocky points, and an unbroken
         * ribbon of tan round every island is the most obviously *drawn* thing
         * a chart can have. Ice keeps none of it — a polar shore is not a beach
         * — and the fen keeps a third, because a coast that is already mud has
         * little strand to show.
         */
        const bays = vnoise2(fx * 0.09, fy * 0.09, 619);
        const sandy = kind === 'ice' ? 0 : kind === 'marsh' ? 0.35 : 1;
        over(203, 180, 133, t * t * (1 - cliff) * sandy * (0.55 + bays * 0.7) * 0.82);

        /**
         * The cliff itself: a rock face at the water and a lit lip above it.
         *
         * The face takes the outer half of the band and is bright or dark by
         * which way it points; the lip is a narrow bright line a couple of units
         * inland, the crest catching the light. It is drawn here rather than in
         * the hillshade because the hillshade's vertical exaggeration is
         * deliberately gentle at sea level — a sea cliff is invisible to it and
         * unmistakable to anybody trying to land a boat.
         */
        const face = Math.max(0, t - 0.42) / 0.58;
        if (facing > 0) over(238, 230, 206, face * face * cliff * facing * 0.5);
        else over(20, 18, 15, face * face * cliff * -facing * 0.62);
        over(246, 238, 214, Math.max(0, 1 - Math.abs(s - 2.4) / 1.8) * cliff * Math.max(0, facing) * 0.34);
      }

      /**
       * Standing water — the fen and the mere, off one field.
       *
       * `lakeDepth` is the depth the river pass's own depression fill had to
       * add to make the world drain, so a wet cell here is a cell water cannot
       * leave and its surface is the spill point the outlet stream runs from.
       * Measured, **7,019 land cells hold water — 12.0% of the land — in 39
       * separate bodies, with a median depth of 0.034 and a maximum of 0.326**,
       * and 12% of a continent under open water would be a swamp planet. So the
       * one field is read at two depths and says two different things:
       *
       *   shallow   ground that is *wet*. A dull green-grey damp, and the fen's
       *             reed fabric already sits on top of it. This is what "marsh"
       *             looked like before: the fabric and nothing else.
       *   deep      standing water. The sea's own slate, with a pale margin
       *             band at its edge for the same reason the shelf has one, and
       *             the strand's rule about the sun on water applies here too.
       *
       * The threshold is wobbled by a coarse noise, because a lake drawn at one
       * depth is a *contour*, and a contour is the one mark that unmistakably
       * says computed. Same argument, and the same fix, as the ragged snowline.
       */
      const wet =
        sampleField(wetField, fx, fy) * (0.82 + vnoise2(fx * 0.11, fy * 0.11, 733) * 0.42);
      if (wet > FEN_FROM) {
        const damp = Math.max(0, Math.min(1, (wet - FEN_FROM) / (FEN_FULL - FEN_FROM)));
        const open = Math.max(0, Math.min(1, (wet - MERE_FROM) / (MERE_FULL - MERE_FROM)));
        over(74, 96, 88, damp * (1 - open) * 0.34);
        if (open > 0) {
          over(28, 50, 60, open * 0.94);
          // Brightest where the water is shallowest inside its own margin, which
          // is a shelf by another name.
          over(112, 152, 170, open * (1 - open) * 4 * 0.34);
        }
      }

      /**
       * Snow, as a colour rather than as a highlight.
       *
       * The hillshade already keeps a peak pale; what it cannot do is make the
       * ground *cold*, because it is an overlay of white and grey over whatever
       * the province fill happens to be — so an ice province and a bright chalk
       * plain came out of it the same. This is the other half: a faintly blue
       * white laid on the ground before the light touches it, so that the relief
       * then lights snow instead of lighting rock and calling it snow.
       */
      const snow = snowAt(height[k], fx, fy, originY + fy * cell, kind === 'ice');
      if (snow > 0) {
        /**
         * Wind-scoured, and the patchiness is the point.
         *
         * A snowfield is not one tone: it is drifts, scoured shoulders, and the
         * rock showing through wherever the wind gets at it. Two noises, one at
         * the scale of a corrie and one at the scale of a drift, keep it off
         * being a flat wash — which is what "white paint" means, and what this
         * exists to stop being.
         */
        const drift = fbm(fx, fy, 0.13, 331, 3) * 0.55 + fbm(fx, fy, 0.5, 337, 2) * 0.45;
        over(226, 234, 241, Math.max(0, Math.min(1, snow * (0.62 + drift * 0.76))) * 0.82);
      }

      put();
    }
  }

  ctx.putImageData(img, 0, 0);
  try {
    groundCached = canvas.toDataURL('image/png');
  } catch {
    return '';
  }
  return groundCached;
}

// ── The quilt: the ground's own colour, per cell ────────────────────────────
//
// **What was wrong.** The colour of the ground was a flat tint per *province*.
// The relief is per cell, the fabric is per cell, the strand is per cell — the
// colour under all three was not, so two neighbouring provinces of different
// ground met at a hard polygon edge that had nothing to do with where the
// ground changes. `npm run coastline` counts it: **64.0% of this world's inland
// frontier — 13,830 map units of it — separates two different grounds**, and the
// longest single one runs fifty-one units of ice against mountain in a straight
// drawn line. That is a political boundary doing a job that belongs to the
// weather.
//
// **What replaces it.** The tint is painted here, per cell, and `territoryLayer`
// keeps only the politics — it draws the relation's ink over this at exactly the
// alpha it used to mix at, so the arithmetic on the political channel is
// unchanged to the last bit and only the ground underneath it moves.
//
// **How the boundary is decided**, because "blur it" is not an answer:
//
//   1. Each terrain gets a weight raster — one where a cell's province is that
//      terrain, zero elsewhere — and each is blurred at four map units. Inside
//      a province nothing changes: the blur of a constant is that constant.
//   2. Every weight is then leaned on by the **ground's own elevation**. Each
//      terrain's mean height is measured off the raster (printed by the
//      instrument, and the spread is real: mountain runs far above marsh), and
//      a cell prefers, among the terrains contending for it, the one whose
//      height it is closest to. So where a mountain province meets an icefield,
//      the rock comes down the high ground and the ice takes the low — which is
//      what those two things do.
//   3. The lookup is displaced by a noise field before it is read, so the
//      hand-over interlocks in bays and tongues instead of being an airbrushed
//      ramp. Same trick, and the same reason, as the bays that break the strand.
//
// **The edge at the water is not this layer's to draw.** The alpha runs a
// couple of cells past the mask on purpose and the whole image is clipped to the
// vector coastline in the SVG, so the boundary between ground and sea is the
// drawn coast at every zoom rather than a staircase of one-unit pixels.

/** Blur radius for the terrain weights, in raster cells. Two is four map units. */
const QUILT_BLUR = 2;
/** …and how far the lookup wanders, in cells, and at what wavelength. */
const QUILT_WANDER = 2.4;
/** How sharply the contest is resolved. 1 is a wash; large is a hard edge. */
const QUILT_GAIN = 2.6;
/** How far a terrain's own mean height leans the contest its way. */
const QUILT_LEAN = 0.16;

let quiltCached = '';

/**
 * The per-terrain mean elevation, measured off the raster.
 *
 * Exported because it is the number the boundary rule above is built on, and a
 * rule whose numbers nobody can print is a rule nobody can argue with.
 */
export function terrainHeights(): Map<ProvinceTerrain, number> {
  const { cols, rows, owner, height } = RASTER;
  const sum = new Map<ProvinceTerrain, number>();
  const count = new Map<ProvinceTerrain, number>();
  const kindOf = new Map<number, ProvinceTerrain>();
  for (let k = 0; k < cols * rows; k++) {
    const p = owner[k];
    if (p < 0) continue;
    let kind = kindOf.get(p);
    if (kind === undefined) {
      kind = provinceDef(p).terrain;
      kindOf.set(p, kind);
    }
    sum.set(kind, (sum.get(kind) ?? 0) + height[k]);
    count.set(kind, (count.get(kind) ?? 0) + 1);
  }
  const out = new Map<ProvinceTerrain, number>();
  for (const [kind, s] of sum) out.set(kind, s / (count.get(kind) ?? 1));
  return out;
}

/**
 * The biome wash as a data URL.
 *
 * `inkOf` is handed in rather than imported, because the chart's colour language
 * lives in `worldmap.ts` and this module may not reach back up into it. Cached
 * on the first call for the same reason every other bake here is: it is a
 * function of the raster and the province table, and nothing in a campaign can
 * move either.
 */
export function biomeImage(inkOf: (kind: ProvinceTerrain) => number): string {
  if (quiltCached !== '') return quiltCached;
  const { cols, rows, owner, height } = RASTER;
  const n = cols * rows;
  const w = cols * SHORE_SS;
  const h = rows * SHORE_SS;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  // ── The contenders ────────────────────────────────────────────────────────
  const kindOf = new Map<number, ProvinceTerrain>();
  const kinds: ProvinceTerrain[] = [];
  const index = new Map<ProvinceTerrain, number>();
  const cellKind = new Int8Array(n).fill(-1);
  for (let k = 0; k < n; k++) {
    const p = owner[k];
    if (p < 0) continue;
    let kind = kindOf.get(p);
    if (kind === undefined) {
      kind = provinceDef(p).terrain;
      kindOf.set(p, kind);
    }
    let i = index.get(kind);
    if (i === undefined) {
      i = kinds.length;
      kinds.push(kind);
      index.set(kind, i);
    }
    cellKind[k] = i;
  }
  const T = kinds.length;
  if (T === 0) return '';

  const weight: Float32Array[] = [];
  for (let t = 0; t < T; t++) weight.push(new Float32Array(n));
  for (let k = 0; k < n; k++) if (cellKind[k] >= 0) weight[cellKind[k]][k] = 1;

  // A separable box blur, twice, which is a fair Gaussian and costs four sweeps
  // over the raster per terrain rather than a kernel per cell.
  const scratch = new Float32Array(n);
  for (const f of weight) {
    for (let pass = 0; pass < 2; pass++) {
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          let s = 0;
          let m = 0;
          for (let d2 = -QUILT_BLUR; d2 <= QUILT_BLUR; d2++) {
            const cc = c + d2;
            if (cc < 0 || cc >= cols) continue;
            s += f[r * cols + cc];
            m++;
          }
          scratch[r * cols + c] = s / m;
        }
      }
      for (let c = 0; c < cols; c++) {
        for (let r = 0; r < rows; r++) {
          let s = 0;
          let m = 0;
          for (let d2 = -QUILT_BLUR; d2 <= QUILT_BLUR; d2++) {
            const rr = r + d2;
            if (rr < 0 || rr >= rows) continue;
            s += scratch[rr * cols + c];
            m++;
          }
          f[r * cols + c] = s / m;
        }
      }
    }
  }

  // ── The lean: each terrain's own mean height, off the raster ──────────────
  const means = terrainHeights();
  const hbar = kinds.map((kind) => means.get(kind) ?? 0.5);

  // ── One colour per cell, resolved ─────────────────────────────────────────
  const rr = new Float32Array(n);
  const gg = new Float32Array(n);
  const bb = new Float32Array(n);
  const ink = kinds.map(inkOf);
  const solid = new Uint8Array(n);
  for (let k = 0; k < n; k++) {
    if (owner[k] < 0) continue;
    solid[k] = 1;
    const hh = height[k];
    let total = 0;
    let cr = 0;
    let cg = 0;
    let cb = 0;
    for (let t = 0; t < T; t++) {
      const base = weight[t][k];
      if (base <= 0.002) continue;
      // Nearer this terrain's own country in height, the more of the cell it
      // takes. Floored, so a terrain that is *there* never vanishes outright.
      const lean = Math.max(0.25, 1 - Math.abs(hh - hbar[t]) / QUILT_LEAN);
      const v = Math.pow(base * lean, QUILT_GAIN);
      total += v;
      cr += ((ink[t] >> 16) & 255) * v;
      cg += ((ink[t] >> 8) & 255) * v;
      cb += (ink[t] & 255) * v;
    }
    if (total <= 0) continue;
    rr[k] = cr / total;
    gg[k] = cg / total;
    bb[k] = cb / total;
  }

  /**
   * …and spread a little way out to sea.
   *
   * The clip against the drawn coastline is what decides where this layer stops,
   * and the drawn coastline is not the raster's edge — so the colour has to
   * exist a few cells beyond the mask or the clip would cut into ground that has
   * none. Three dilations at two units apiece is six, which covers the warp.
   */
  for (let pass = 0; pass < 3; pass++) {
    const takenR = rr.slice();
    const takenG = gg.slice();
    const takenB = bb.slice();
    const wasSolid = solid.slice();
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const k = r * cols + c;
        if (wasSolid[k]) continue;
        let m = 0;
        let ar = 0;
        let ag = 0;
        let ab = 0;
        for (let dy = -1; dy <= 1; dy++) {
          const ry = r + dy;
          if (ry < 0 || ry >= rows) continue;
          for (let dx = -1; dx <= 1; dx++) {
            const rx = c + dx;
            if (rx < 0 || rx >= cols) continue;
            const j = ry * cols + rx;
            if (!wasSolid[j]) continue;
            ar += takenR[j];
            ag += takenG[j];
            ab += takenB[j];
            m++;
          }
        }
        if (m === 0) continue;
        rr[k] = ar / m;
        gg[k] = ag / m;
        bb[k] = ab / m;
        solid[k] = 1;
      }
    }
  }

  const img = ctx.createImageData(w, h);
  const d = img.data;
  const sampleRGB = (f: Float32Array, x: number, y: number): number => {
    const cx = Math.max(0, Math.min(cols - 1.001, x));
    const cy = Math.max(0, Math.min(rows - 1.001, y));
    const x0 = cx | 0;
    const y0 = cy | 0;
    const tx = cx - x0;
    const ty = cy - y0;
    const k = y0 * cols + x0;
    return (
      (f[k] + (f[k + 1] - f[k]) * tx) * (1 - ty) +
      (f[k + cols] + (f[k + cols + 1] - f[k + cols]) * tx) * ty
    );
  };

  for (let py = 0; py < h; py++) {
    for (let px = 0; px < w; px++) {
      const src = planeSource(px / SHORE_SS, py / SHORE_SS);
      const o = (py * w + px) * 4;
      const mx = Math.max(0, Math.min(cols - 1, src.x | 0));
      const my = Math.max(0, Math.min(rows - 1, src.y | 0));
      if (!solid[my * cols + mx]) {
        d[o + 3] = 0;
        continue;
      }
      // The wander, which is what stops the hand-over from being an airbrush.
      const jx = src.x + (vnoise2(src.x * 0.09, src.y * 0.09, 811) - 0.5) * 2 * QUILT_WANDER;
      const jy = src.y + (vnoise2(src.x * 0.09, src.y * 0.09, 823) - 0.5) * 2 * QUILT_WANDER;
      d[o] = sampleRGB(rr, jx, jy) | 0;
      d[o + 1] = sampleRGB(gg, jx, jy) | 0;
      d[o + 2] = sampleRGB(bb, jx, jy) | 0;
      d[o + 3] = 255;
    }
  }

  ctx.putImageData(img, 0, 0);
  try {
    quiltCached = canvas.toDataURL('image/png');
  } catch {
    return '';
  }
  return quiltCached;
}

/**
 * The `<image>` element for the biome wash, in world units.
 *
 * Clipped to the drawn coastline rather than to its own alpha, so the edge of
 * the world is a vector at every zoom. `wm-land` is defined once in `chartBed`.
 */
export function biomeLayer(inkOf: (kind: ProvinceTerrain) => number): string {
  const href = biomeImage(inkOf);
  if (href === '') return '';
  return (
    `<image class="wm-biome" href="${href}" clip-path="url(#wm-land)" ` +
    `x="${WORLD.originX}" y="${WORLD.originY}" ` +
    `width="${WORLD.w}" height="${WORLD.h}" preserveAspectRatio="none" />`
  );
}

// ── The shoreline itself, as a pen rather than as a bake ────────────────────

/**
 * The coast, cut into runs of cliff and runs of strand.
 *
 * The bake above says what the ground near the water is; this says what the
 * *line* is, and a chart needs both. A raster laid at two pixels to a cell goes
 * soft four notches into the zoom — deliberately, it is the same trade the
 * hillshade makes — and the shoreline is the one mark on this sheet that has to
 * stay a line at every zoom, because it is the answer to the only question the
 * whole game is about: which ground can you walk to.
 *
 * So the coastline is split where its character changes and each kind gets its
 * own pen: an ironbound coast gets a heavy dark line with hachures combed
 * inland off it, which is the cliff symbol every survey chart has used for two
 * hundred years, and a low coast gets a fine pale line with the strand doing the
 * talking behind it.
 *
 * **The cliffness used to be sampled at the warped point**, which the first
 * pass wrote down as a known small lie: the field is smoothed over eight map
 * units and the warp moves a point by up to five, so the answer was right about
 * which headland it belonged to and could not have been trusted about anything
 * finer. It is not a lie any more. The sample goes through `planeSource`, so a
 * run of cliff is read at the place on the raster the drawn line was traced
 * from, and the hachures are combed off the same answer.
 */

/** Above this, the shore is drawn as cliff. Hysteresis of ±0.08 around it. */
const CLIFF_CUT = 0.44;
/** Runs shorter than this, in map units, are absorbed by their neighbours. */
const RUN_MIN = 9;
/**
 * Hachure spacing and length, in **map units** — a ground mark, like the
 * terrain hatching, so it grows with the ground it describes.
 *
 * Which means it has a floor and a ceiling and both are real. At the fit view a
 * unit is about a pixel and the ticks are correctly invisible, with the baked
 * strand and the relief carrying the shore instead. At the *bottom* of the zoom
 * the view is 40 units wide over a thousand pixels — twenty-five pixels to the
 * unit — and the first cut of this, at 1.7 units long and 0.42 thick, arrived
 * as forty-pixel black bars stuck out into the sea. Photographed, it looked
 * like a row of railway sleepers. Shorter, and the thickness moved to glass
 * units, and hidden altogether at the closest tier where the coastline is doing
 * the work on its own.
 */
const TICK_GAP = 2.6;
const TICK_LEN = 1.15;

export interface CoastRun {
  cliff: boolean;
  /** The run of shoreline. */
  d: string;
  /** The hachures combed inland off it, or '' for a low shore. */
  ticks: string;
}

let runCache: CoastRun[] | null = null;

export function coastRuns(): CoastRun[] {
  if (runCache) return runCache;
  const { originX, originY, cell } = RASTER;
  const cliffField = shoreCliff();
  const sd = shoreDistance();
  const at = (field: Float32Array, p: Pt): number => {
    const s = planeSource((p.x - originX) / cell, (p.y - originY) / cell);
    return sampleField(field, s.x - 0.5, s.y - 0.5);
  };

  const out: CoastRun[] = [];
  for (const ring of geometry().coast) {
    if (ring.length < 8) continue;
    const n = ring.length;
    // Smoothed along the ring, because a single point's answer flickers and a
    // shoreline that alternates cliff and beach every two units is noise drawn
    // at full weight rather than a coast.
    const raw = ring.map((p) => at(cliffField, p));
    const c: number[] = [];
    for (let i = 0; i < n; i++) {
      let s = 0;
      for (let k = -3; k <= 3; k++) s += raw[(i + k + n * 2) % n];
      c.push(s / 7);
    }

    // Classify with hysteresis, then absorb any run too short to be a coast.
    const flag: boolean[] = [];
    let on = c[0] > CLIFF_CUT;
    for (let i = 0; i < n; i++) {
      if (on && c[i] < CLIFF_CUT - 0.08) on = false;
      else if (!on && c[i] > CLIFF_CUT + 0.08) on = true;
      flag.push(on);
    }
    const len = (a: number, b: number): number => {
      let s = 0;
      for (let i = a; i < b; i++) {
        s += Math.hypot(ring[(i + 1) % n].x - ring[i].x, ring[(i + 1) % n].y - ring[i].y);
      }
      return s;
    };
    let i = 0;
    while (i < n) {
      let j = i + 1;
      while (j < n && flag[j] === flag[i]) j++;
      if (len(i, j) < RUN_MIN && i > 0) for (let k = i; k < j; k++) flag[k] = flag[i - 1];
      i = j;
    }

    i = 0;
    while (i < n) {
      let j = i + 1;
      while (j < n && flag[j] === flag[i]) j++;
      const seg = ring.slice(i, Math.min(j + 1, n));
      if (seg.length >= 2) {
        let d = `M${seg[0].x.toFixed(2)} ${seg[0].y.toFixed(2)}`;
        for (let k = 1; k < seg.length; k++) d += `L${seg[k].x.toFixed(2)} ${seg[k].y.toFixed(2)}`;
        out.push({ cliff: flag[i], d, ticks: flag[i] ? hachure(seg, sd, at) : '' });
      }
      i = j;
    }
  }
  runCache = out;
  return out;
}

/**
 * Ticks combed off a run of cliff, pointing inland.
 *
 * Which side is inland is asked of the distance field rather than of the ring's
 * winding, because a coastline complex has outer rings and hole rings and their
 * windings are opposite by construction — a hachure that trusted the winding
 * would comb every inland sea's cliffs out into the water.
 */
function hachure(
  seg: Pt[],
  sd: Float32Array,
  at: (f: Float32Array, p: Pt) => number,
): string {
  let d = '';
  let carried = 0;
  for (let i = 1; i < seg.length; i++) {
    const ax = seg[i - 1].x;
    const ay = seg[i - 1].y;
    const dx = seg[i].x - ax;
    const dy = seg[i].y - ay;
    const l = Math.hypot(dx, dy);
    if (l < 1e-6) continue;
    carried += l;
    if (carried < TICK_GAP) continue;
    carried = 0;
    const mx = ax + dx / 2;
    const my = ay + dy / 2;
    let nx = -dy / l;
    let ny = dx / l;
    if (at(sd, { x: mx + nx * 2.5, y: my + ny * 2.5 }) < at(sd, { x: mx - nx * 2.5, y: my - ny * 2.5 })) {
      nx = -nx;
      ny = -ny;
    }
    d +=
      `M${mx.toFixed(2)} ${my.toFixed(2)}` +
      `L${(mx + nx * TICK_LEN).toFixed(2)} ${(my + ny * TICK_LEN).toFixed(2)}`;
  }
  return d;
}

/** The `<image>` element for the shore and the snow, in world units. */
export function groundLayer(): string {
  const href = groundImage();
  if (href === '') return '';
  return (
    `<image class="wm-ground" href="${href}" ` +
    `x="${WORLD.originX}" y="${WORLD.originY}" ` +
    `width="${WORLD.w}" height="${WORLD.h}" preserveAspectRatio="none" />`
  );
}
